/**
 * 全局 Toast 系统
 * - 四种类型：success / error / warning / info
 * - 右上角固定定位，最多同时显示 3 条（新消息挤掉最旧一条）
 * - 默认 3 秒自动消失，可手动关闭
 * - 品牌风格：圆角 xl、阴影、图标 + 文字（纸墨设计系统 CSS 变量）
 *
 * 用法：
 *   <ToastProvider> ... <ToastContainer /> </ToastProvider>   // 根组件挂载一次
 *   const { showToast } = useToast()
 *   showToast('success', '上传成功')
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

const MAX_VISIBLE = 3
const DEFAULT_DURATION = 3000

const ToastContext = createContext(null)

const typeConfig = {
  success: {
    accent: 'var(--color-success, #059669)',
    iconClass: 'text-emerald-500',
    icon: 'M5 13l4 4L19 7',
  },
  error: {
    accent: '#dc2626',
    iconClass: 'text-red-500',
    icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z',
  },
  warning: {
    accent: '#d97706',
    iconClass: 'text-amber-500',
    icon: 'M12 9v4m0 4h.01M10.29 3.86l-8.15 14.14c-.77 1.34.19 3.02 1.73 3.02h16.26c1.54 0 2.5-1.68 1.73-3.02L13.71 3.86c-.77-1.33-2.69-1.33-3.46 0z',
  },
  info: {
    accent: 'var(--color-brand-600, #C1751B)',
    iconClass: 'text-brand-600',
    icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const idRef = useRef(0)
  const timersRef = useRef(new Map())

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
  }, [])

  const showToast = useCallback((type, message, duration = DEFAULT_DURATION) => {
    const id = ++idRef.current
    setToasts((prev) => {
      const next = [...prev, { id, type: typeConfig[type] ? type : 'info', message }]
      // 超出可见上限时挤掉最旧的一条（队列语义）
      while (next.length > MAX_VISIBLE) {
        const dropped = next.shift()
        const timer = timersRef.current.get(dropped.id)
        if (timer) {
          clearTimeout(timer)
          timersRef.current.delete(dropped.id)
        }
      }
      return next
    })
    if (duration > 0) {
      timersRef.current.set(id, setTimeout(() => dismiss(id), duration))
    }
    return id
  }, [dismiss])

  // 卸载时清理全部计时器
  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach((t) => clearTimeout(t))
      timers.clear()
    }
  }, [])

  const value = useMemo(() => ({ toasts, showToast, dismiss }), [toasts, showToast, dismiss])

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

/** 获取 showToast(type, message, duration?) —— type: success | error | warning | info */
export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // 未包裹 Provider 时降级为 console，避免页面崩溃
    return {
      toasts: [],
      showToast: (type, message) => console.warn(`[toast:${type}]`, message),
      dismiss: () => {},
    }
  }
  return ctx
}

/** Toast 队列渲染容器，放在 ToastProvider 内部渲染一次即可 */
export function ToastContainer() {
  const { toasts, dismiss } = useToast()

  if (toasts.length === 0) return null

  return (
    <div className="fixed top-5 right-5 z-[100] flex flex-col gap-2.5 items-end pointer-events-none">
      {toasts.map((t) => {
        const cfg = typeConfig[t.type] || typeConfig.info
        return (
          <div
            key={t.id}
            className="pointer-events-auto flex items-center gap-2.5 max-w-sm pl-3.5 pr-2 py-3 rounded-xl bg-white border border-line shadow-lg animate-slide-right"
            style={{ borderLeftWidth: 3, borderLeftColor: cfg.accent }}
            role="status"
          >
            <svg className={`w-4 h-4 flex-shrink-0 ${cfg.iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={cfg.icon} />
            </svg>
            <span className="text-[13px] font-medium text-ink-primary leading-snug">{t.message}</span>
            <button
              onClick={() => dismiss(t.id)}
              aria-label="关闭提示"
              className="ml-1 p-1 rounded-md text-ink-tertiary hover:text-ink-primary hover:bg-surface-sunken transition-colors flex-shrink-0"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )
      })}
    </div>
  )
}

export default ToastProvider
